import { EventSubscriber, HybridLogicalClockTimestampProvider, PatchCreator, VerdantError, noop, } from '@verdant-web/common';
import { ShutdownHandler } from '../context/ShutdownHandler.js';
import { Time } from '../context/Time.js';
import { FakeWeakRef } from '../FakeWeakRef.js';
import { IdbPersistence } from '../persistence/idb/idbPersistence.js';
import { deleteAllDatabases } from '../persistence/idb/util.js';
import { initializePersistence } from '../persistence/persistence.js';
import { UndoHistory } from '../UndoHistory.js';
import { Client } from './Client.js';
/**
 * Since storage initialization is async, this class wraps the core
 * Storage creation promise and exposes some metadata which can
 * be useful immediately.
 */
export class ClientDescriptor {
    get namespace() {
        return this._namespace;
    }
    constructor(init) {
        this.init = init;
        this._initializing = false;
        this.initialize = async (init) => {
            // if server-side and no alternative IndexedDB implementation was provided,
            // we can't initialize the storage
            if (typeof window === 'undefined' && !init.environment) {
                throw new Error('A Verdant client was initialized in an environment without a global Window or `environment` configuration. If you are using verdant in a server-rendered framework, you must enforce that all clients are initialized on the client-side, or you must provide some mock interface of the environment to the ClientDescriptor options.');
            }
            if (this._initializing || this._resolvedValue) {
                return this._readyPromise;
            }
            this._initializing = true;
            try {
                const time = new Time(new HybridLogicalClockTimestampProvider(), init.schema.version);
                const environment = init.environment || defaultBrowserEnvironment;
                let ctx = {
                    closing: false,
                    entityEvents: new EventSubscriber(),
                    globalEvents: new EventSubscriber(),
                    internalEvents: new EventSubscriber(),
                    log: init.log || noop,
                    migrations: init.migrations,
                    namespace: init.namespace,
                    originalNamespace: init.namespace,
                    schema: init.schema,
                    oldSchemas: init.oldSchemas,
                    time,
                    undoHistory: init.undoHistory || new UndoHistory(),
                    weakRef: (val) => init.EXPERIMENTAL_weakRefs
                        ? new WeakRef(val)
                        : new FakeWeakRef(val),
                    patchCreator: new PatchCreator(() => time.now),
                    config: {
                        files: init.files,
                        sync: init.sync,
                        persistence: {
                            disableRebasing: init.disableRebasing,
                            rebaseTimeout: init.rebaseTimeout,
                        },
                        queries: init.queries,
                    },
                    persistence: init.persistence || new IdbPersistence(environment.indexedDB),
                    environment,
                    persistenceShutdownHandler: new ShutdownHandler(init.log),
                    pauseRebasing: false,
                    getClient() {
                        throw new VerdantError(VerdantError.Code.Unexpected, undefined, 'Client not yet initialized. This is a Verdant bug, please report it.');
                    },
                };
                ctx.log('info', 'Initializing client', {
                    namespace: ctx.namespace,
                    version: init.schema.version,
                    persistence: ctx.persistence.name,
                });
                const context = await initializePersistence(ctx);
                const client = new Client(context);
                this.resolveReady(client);
                this._resolvedValue = client;
                return client;
            }
            catch (err) {
                if (err instanceof Error) {
                    this.rejectReady(err);
                    throw err;
                }
                else {
                    throw new Error('Unknown error initializing storage');
                }
            }
            finally {
                this._initializing = false;
            }
        };
        this.open = () => this.initialize(this.init);
        this.close = async () => {
            if (this._resolvedValue) {
                this._resolvedValue.close();
            }
            if (this._initializing) {
                (await this._readyPromise).close();
            }
        };
        this.__dangerous__resetLocal = async () => {
            await deleteAllDatabases(this.namespace);
        };
        this._readyPromise = new Promise((resolve, reject) => {
            this.resolveReady = resolve;
            this.rejectReady = reject;
        });
        this._namespace = init.namespace;
    }
    get current() {
        // exposing an immediate value if already resolved lets us
        // skip the promise microtask when accessing this externally if
        // the initialization has been completed.
        return this._resolvedValue;
    }
    get readyPromise() {
        return this._readyPromise;
    }
    get schema() {
        return this.init.schema;
    }
}
const defaultBrowserEnvironment = {
    WebSocket: typeof WebSocket !== 'undefined' ? WebSocket : undefined,
    fetch: typeof window !== 'undefined' ? window.fetch.bind(window) : fetch,
    indexedDB: typeof indexedDB !== 'undefined' ? indexedDB : undefined,
};
//# sourceMappingURL=ClientDescriptor.js.map